import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";

const HIDDEN_ROUTES = ["/create", "/preview", "/auth", "/upload"];
const COOKIE_KEY = "cookie-consent";
const HOME_ROUTES = ["/", "/index"];
// Show sticky CTA on phones AND tablets (incl. iPad Pro portrait at 1024px).
const STICKY_BREAKPOINT = 1024;

const useIsMobileOrTablet = () => {
  const [match, setMatch] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= STICKY_BREAKPOINT;
  });
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${STICKY_BREAKPOINT}px)`);
    const onChange = () => setMatch(window.innerWidth <= STICKY_BREAKPOINT);
    mql.addEventListener("change", onChange);
    onChange();
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return match;
};

const StickyMobileCTA = () => {
  const isMobileOrTablet = useIsMobileOrTablet();
  const { pathname } = useLocation();
  const isHome = HOME_ROUTES.includes(pathname);
  const [scrolled, setScrolled] = useState(false);
  const [cookieHandled, setCookieHandled] = useState(() => {
    try { return !!localStorage.getItem(COOKIE_KEY); } catch { return false; }
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Re-check cookie consent periodically
  useEffect(() => {
    const interval = setInterval(() => {
      try { setCookieHandled(!!localStorage.getItem(COOKIE_KEY)); } catch {}
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (!isMobileOrTablet) return null;
  if (HIDDEN_ROUTES.some((r) => pathname.startsWith(r))) return null;

  // On homepage: always show immediately for discoverability.
  // On other pages: show after scrolling 400px.
  const visible = isHome ? true : scrolled;
  if (!visible) return null;

  // Stack above cookie banner when both visible.
  // Cookie banner ~52px tall; lift sticky CTA above it.
  const bottomOffset = !cookieHandled ? "bottom-14" : "bottom-0";

  return (
    <div className={`fixed ${bottomOffset} inset-x-0 z-40 p-2 sm:p-3 bg-background/95 backdrop-blur-sm border-t border-border/40 animate-fade-in-up transition-all`}>
      <div className="max-w-md mx-auto">
        <Link to="/create" aria-label="Start my native plant plan">
          <Button variant="hero" size="default" className="w-full gap-2 min-h-[48px]">
            Start My Plan
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </Link>
        <p className="text-[11px] sm:text-xs text-muted-foreground text-center mt-1">Free to preview · Takes less than a minute</p>
      </div>
    </div>
  );
};

export default StickyMobileCTA;

